import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/forms/TextField';
import { SelectField } from '@/components/forms/SelectField';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { FIELD_LABELS, MAX_FOUNDING_TEAM, PAST_EXITS_OPTIONS } from '../constants';

const S = UI_STRINGS.STARTUP_PROFILE;
/**
 * The member field labels the create wizard already uses, so the same field is
 * never labelled two different ways across the wizard, the founder's profile
 * editor, and the admin console.
 */
const F = FIELD_LABELS;

/**
 * One founding-team member as a FORM value.
 *
 * `pastExits` is `'yes' | 'no'`, not a boolean, because the control is a native
 * `<select>` whose DOM value can only ever be a string - a boolean here fails
 * validation silently on every submit. It is converted to a real boolean only
 * when the payload is built.
 */
export interface FoundingTeamMemberValues {
  name?: string;
  title?: string;
  linkedinUrl?: string;
  pastExits?: 'yes' | 'no';
  /**
   * True for members hydrated from a saved profile. They render as a compact
   * read-only summary with a Remove action; only rows added here get the full
   * form. Re-editing a saved member is not part of either surface's scope.
   */
  isExisting?: boolean;
  /**
   * Stable React key for a row added here, assigned on append.
   *
   * Index-based keys shift when a row is removed mid-list, so React reuses the
   * surviving row's DOM for a different member - the reason this was added was
   * a `FileUploadField` handing its local upload state to the row below it
   * (that control is gone as of MYN-175). The id still keeps focus, scroll
   * position and any future per-row state attached to the member it belongs
   * to. Saved rows need no id - they are read-only summaries.
   *
   * A form-values-only field, like `isExisting`; both schemas strip it.
   */
  rowId?: string;
}

/** Per-field messages for one member row, in the shape React Hook Form produces. */
export interface FoundingTeamMemberErrors {
  name?: { message?: string };
  title?: { message?: string };
  linkedinUrl?: { message?: string };
  pastExits?: { message?: string };
}

/**
 * Surface tone. The founder's profile page is the light brand canvas; the admin
 * console drawer is themed by `data-theme` and must use the neutral surface
 * tokens, or these rows render as a white card punched through a dark panel.
 */
export type FoundingTeamEditorTone = 'founder' | 'admin';

interface ToneClasses {
  summary: string;
  panel: string;
  avatar: string;
  name: string;
  meta: string;
  link: string;
  heading: string;
}

const TONE_CLASSES: Record<FoundingTeamEditorTone, ToneClasses> = {
  founder: {
    summary: 'bg-slate/10 rounded-card p-6',
    // Inline Panel (ui-guidelines.md §11): 1.5px green border, 20px radius,
    // 32px padding, white background - a slide-down "add new member" panel,
    // not a plain nested card.
    panel: 'rounded-card border-[1.5px] border-green bg-white p-8',
    avatar: 'bg-green text-white',
    name: 'text-navy font-semibold',
    meta: 'text-slate text-p6',
    link: 'text-dkgreen hover:text-green text-p6',
    heading: 'text-navy font-sans font-semibold',
  },
  admin: {
    summary: 'border-border-subtle bg-surface-raised rounded-lg border px-4 py-3',
    panel: 'border-border-subtle bg-surface-raised rounded-lg border p-4',
    avatar: 'bg-green text-white',
    name: 'text-text-primary font-medium',
    meta: 'text-text-secondary text-xs',
    link: 'text-green hover:opacity-80 text-xs',
    heading: 'text-text-primary font-medium',
  },
};

export interface FoundingTeamEditorProps {
  members: FoundingTeamMemberValues[];
  onChange: (next: FoundingTeamMemberValues[]) => void;
  /**
   * Messages for one row, looked up by index rather than passed as an array:
   * React Hook Form's `errors.foundingTeam` is a `Merge<FieldError, ...[]>`,
   * which indexes cleanly at the call site but is not assignable to any plain
   * array type here. A lookup keeps this component free of that library's
   * types without a cast.
   */
  memberError?: (index: number) => FoundingTeamMemberErrors | undefined;
  tone?: FoundingTeamEditorTone;
}

/** Row ids only have to be unique within one list, so a counter is enough. */
let rowIdCounter = 0;
function makeRowId(): string {
  rowIdCounter += 1;
  return `new-${rowIdCounter}`;
}

function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

/**
 * Avatar-circle + name/title/linkedin/past-exit summary for an already-saved
 * member. Exported so the read-only profile view renders saved members
 * identically to the way the editor shows them.
 */
export function FoundingTeamMemberSummary({
  member,
  tone = 'founder',
}: {
  member: FoundingTeamMemberValues;
  tone?: FoundingTeamEditorTone;
}) {
  return <MemberSummary member={member} classes={TONE_CLASSES[tone]} />;
}

function MemberSummary({
  member,
  classes,
  showPastExit = true,
}: {
  member: FoundingTeamMemberValues;
  classes: ToneClasses;
  /** Off in the editor, where the Past Exits field itself shows the answer. */
  showPastExit?: boolean;
}) {
  const name = member.name ?? '';
  return (
    <div className="flex items-start gap-3">
      <div
        className={`flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${classes.avatar}`}
      >
        {initialsFor(name) || '?'}
      </div>
      <div className="flex flex-col gap-1">
        <p className={classes.name}>{name}</p>
        <p className={classes.meta}>{member.title ?? ''}</p>
        {member.linkedinUrl && (
          <a href={member.linkedinUrl} target="_blank" rel="noreferrer" className={classes.link}>
            {member.linkedinUrl}
          </a>
        )}
        {showPastExit && member.pastExits === 'yes' && (
          <p className={classes.meta}>
            {S.PAST_EXIT_PREFIX}
            {PAST_EXITS_OPTIONS[0].label}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Repeatable founding-team member editor, shared by the founder's own profile
 * form and the admin console's profile-correction form (MYN-132).
 *
 * Controlled by `members`/`onChange` rather than bound to one form's value
 * type, so both callers drive the same implementation instead of maintaining
 * two array editors that drift apart on the rules that matter: at least one
 * member always survives, at most `MAX_FOUNDING_TEAM` exist, and a member's
 * email is never collected (Global Constraints).
 */
export function FoundingTeamEditor({
  members,
  onChange,
  memberError,
  tone = 'founder',
}: FoundingTeamEditorProps) {
  const classes = TONE_CLASSES[tone];
  // A startup profile must keep at least one founder - this guard is what stops
  // Remove from ever emptying the list.
  const canRemove = members.length > 1;

  function updateMember(index: number, patch: Partial<FoundingTeamMemberValues>) {
    onChange(members.map((member, at) => (at === index ? { ...member, ...patch } : member)));
  }

  function removeMember(index: number) {
    onChange(members.filter((_, at) => at !== index));
  }

  return (
    <div className="flex flex-col gap-6">
      {members.map((member, index) => {
        const memberErrors = memberError?.(index);
        const key = member.rowId ?? `saved-${index}`;

        const pastExitsField = (
          <SelectField
            label={F.memberPastExits}
            labelVariant="caps"
            options={PAST_EXITS_OPTIONS}
            value={member.pastExits ?? ''}
            onChange={(event) =>
              updateMember(index, {
                pastExits:
                  event.target.value === '' ? undefined : (event.target.value as 'yes' | 'no'),
              })
            }
            error={memberErrors?.pastExits?.message}
          />
        );

        if (member.isExisting) {
          // A saved member stays a summary, but Past Exits remains editable:
          // the client could not change it once the startup was created
          // (feedback call, 2026-10-26).
          return (
            <div key={key} className={`flex flex-col gap-4 ${classes.summary}`}>
              <div className="flex items-start justify-between gap-4">
                <MemberSummary member={member} classes={classes} showPastExit={false} />
                {canRemove && (
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => removeMember(index)}
                    className="text-red hover:text-red/90 shrink-0 text-sm"
                  >
                    {S.REMOVE_MEMBER}
                  </Button>
                )}
              </div>
              {pastExitsField}
            </div>
          );
        }

        return (
          <div key={key} className={`flex flex-col gap-5 ${classes.panel}`}>
            <div className="flex items-center justify-between">
              <h3 className={classes.heading}>
                {S.MEMBER_TITLE_PREFIX}
                {index + 1}
              </h3>
              {canRemove && (
                <Button
                  type="button"
                  variant="link"
                  onClick={() => removeMember(index)}
                  className="text-red hover:text-red/90 text-sm"
                >
                  {S.REMOVE_MEMBER}
                </Button>
              )}
            </div>
            <TextField
              label={F.memberName}
              labelVariant="caps"
              value={member.name ?? ''}
              onChange={(event) => updateMember(index, { name: event.target.value })}
              error={memberErrors?.name?.message}
            />
            <TextField
              label={F.memberTitle}
              labelVariant="caps"
              value={member.title ?? ''}
              onChange={(event) => updateMember(index, { title: event.target.value })}
              error={memberErrors?.title?.message}
            />
            <TextField
              label={F.memberLinkedin}
              labelVariant="caps"
              type="url"
              value={member.linkedinUrl ?? ''}
              onChange={(event) => updateMember(index, { linkedinUrl: event.target.value })}
              error={memberErrors?.linkedinUrl?.message}
            />
            {pastExitsField}
          </div>
        );
      })}

      {members.length < MAX_FOUNDING_TEAM && (
        <Button
          type="button"
          variant="link"
          className="self-start text-sm"
          // pastExits intentionally omitted - optional Yes/No with no sensible
          // default to pre-fill (matches the create wizard exactly).
          onClick={() =>
            onChange([
              ...members,
              { name: '', title: '', linkedinUrl: '', rowId: makeRowId() },
            ])
          }
        >
          {S.ADD_MEMBER}
        </Button>
      )}
    </div>
  );
}
